import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Pagination from "react-pagination-js";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import { SlRefresh } from "react-icons/sl";
import { AiOutlineDeliveredProcedure } from "react-icons/ai";
import {
  FaEye,
  FaTruck,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
} from "react-icons/fa";

import { MdPendingActions } from "react-icons/md";

import { IoCheckmarkDoneCircle } from "react-icons/io5";
// Helper functions for date formatting
const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInvoiceStatusStyle = (status) => {
  switch (status) {
    case "paid":
      return "bg-green-100 text-green-700 border-green-200";

    case "partial_paid":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";

    case "unpaid":
      return "bg-red-100 text-red-700 border-red-200";

    case "refunded":
      return "bg-purple-100 text-purple-700 border-purple-200";

    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const PaginationTextView = (page, count, total) => {
  let result = page == 1 ? 1 : count * page - count + 1;
  let data = page == 1 ? count : count * page;
  return total == 0
    ? ""
    : `Showing ${result} to ${data > total ? total : data}  of ${total} records`;
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function OrderTrackingList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" represents "All"
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    shipped: 0,
    delivered: 0,
  });

  // Modals for editing shipping date and tracking url
  const [activeOrder, setActiveOrder] = useState(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [shipDateVal, setShipDateVal] = useState("");
  const [trackingUrlVal, setTrackingUrlVal] = useState("");
  const [submittingModal, setSubmittingModal] = useState(false);

  const limit = 10;

  // Search input debouncer
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch orders and calculate status counts
  const fetchOrders = async () => {
    setLoading(true);
    loader(true);
    try {
      const params = {
        page: currentPage,
        limit: limit,
        search: debouncedSearch,
        email: user?.email,
      };
      if (statusFilter) {
        params.status = statusFilter.toLowerCase();
      }

      const res = await ApiClient.get("order-tracking/my-list", params);
      if (res?.success) {
        setOrders(res?.data?.data || []);
        setTotalCount(
          res?.data?.total || (res?.data?.data ? res?.data?.data.length : 0),
        );
        fetchCountsFallback();
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
      loader(false);
    }
  };

  // Fallback to fetch counts for tabs
  const fetchCountsFallback = async () => {
    try {
      const res = await ApiClient.get("order-tracking/my-list", {
        limit: 100000,
        email: user?.email,
      });
      if (res?.success && Array.isArray(res?.data?.data)) {
        const allData = res?.data?.data;
        const counts = {
          all: allData.length,
          pending: allData.filter((o) => o.status?.toLowerCase() === "pending")
            .length,
          shipped: allData.filter((o) => o.status?.toLowerCase() === "shipped")
            .length,
          delivered: allData.filter(
            (o) => o.status?.toLowerCase() === "delivered",
          ).length,
        };
        setStatusCounts(counts);
      }
    } catch (e) {
      console.error("Error fetching status counts fallback:", e);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, statusFilter, debouncedSearch]);

  // Handle status update change
  const handleStatusChange = (orderId, newStatus) => {
    swal({
      title: "Update Status?",
      text: `Are you sure you want to change order status to "${newStatus}"?`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put("order-tracking/status/change", {
          id: orderId,
          status: newStatus.toLowerCase(),
        });
        if (res?.success) {
          swal({
            title: "Success",
            text: "Order status updated successfully.",
            icon: "success",
          });
          fetchOrders();
        } else {
          swal({
            title: "Error",
            text: res?.message || "Failed to update order status.",
            icon: "error",
          });
        }
      } catch (err) {
        console.error("Error updating status:", err);
        swal({
          title: "Error",
          text: "An error occurred while updating the status.",
          icon: "error",
        });
      } finally {
        loader(false);
      }
    });
  };

  // Modal open helpers
  const handleOpenDateModal = (order) => {
    setActiveOrder(order);
    setShipDateVal(order.shipDate || order.shippingDate || "");
    setIsDateModalOpen(true);
  };

  const handleOpenTrackingModal = (order) => {
    setActiveOrder(order);
    setTrackingUrlVal(order.trackingUrl || "");
    setIsTrackingModalOpen(true);
  };

  // Submit shippers/tracking info separately
  const handleDateSubmit = async (e) => {
    e.preventDefault();
    setSubmittingModal(true);
    loader(true);
    try {
      const res = await ApiClient.put("order-tracking/update", {
        id: activeOrder._id || activeOrder.id,
        shipDate: shipDateVal,
      });
      if (res?.success) {
        swal("Success", "Shipping date updated successfully.", "success");
        setIsDateModalOpen(false);
        fetchOrders();
      } else {
        swal(
          "Error",
          res?.message || "Failed to update shipping date.",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      swal("Error", "An error occurred.", "error");
    } finally {
      setSubmittingModal(false);
      loader(false);
    }
  };

  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    setSubmittingModal(true);
    loader(true);
    try {
      const res = await ApiClient.put("order-tracking/update", {
        id: activeOrder._id || activeOrder.id,
        trackingUrl: trackingUrlVal,
      });
      if (res?.success) {
        swal("Success", "Tracking URL updated successfully.", "success");
        setIsTrackingModalOpen(false);
        fetchOrders();
      } else {
        swal(
          "Error",
          res?.message || "Failed to update tracking URL.",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      swal("Error", "An error occurred.", "error");
    } finally {
      setSubmittingModal(false);
      loader(false);
    }
  };

  // Status Badge styles helper
  const getStatusBadgeClass = (status) => {
    const val = (status || "").toLowerCase();
    if (val === "pending") return "bg-amber-50 text-amber-700 border-amber-200";
    if (val === "shipped") return "bg-blue-50 text-blue-700 border-blue-200";
    if (val === "delivered")
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  const getStatusBadgeDot = (status) => {
    const val = (status || "").toLowerCase();
    if (val === "pending") return "bg-amber-500";
    if (val === "shipped") return "bg-blue-500";
    if (val === "delivered") return "bg-emerald-500";
    return "bg-gray-500";
  };

  const STATUS_TABS = [
    { label: "All", value: "", count: statusCounts.all },
    { label: "Pending", value: "pending", count: statusCounts.pending },
    { label: "Shipped", value: "shipped", count: statusCounts.shipped },
    { label: "Delivered", value: "delivered", count: statusCounts.delivered },
  ];

  return (
    <div className="container mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-2 mb-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-gray-900 text-xl xl:text-2xl font-semibold">
            Order Tracking
          </h2>
          <p className="text-gray-500 text-sm">
            Monitor shipping statuses and customer orders
          </p>
        </div>
        <button
          type="button"
          onClick={fetchOrders}
          className="inline-flex items-center gap-1 px-2 sm:px-4 py-2 sm:border border-gray-200 rounded-xl bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors shadow-sm max-sm:!bg-[#115b9d1c] max-sm:text-[#116DAE]"
        >
          <SlRefresh className="text-xl sm:text-lg" />
          <span className="max-sm:hidden">Refresh</span>
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
        {[
          {
            label: "Total Orders",
            value: statusCounts.all,
            bg: "bg-[#115B9D17]",
            textColor: "text-[#115B9D]",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            ),
          },
          {
            label: "Pending Orders",
            value: statusCounts.pending,
            bg: "bg-amber-50",
            textColor: "text-amber-600",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ),
          },
          {
            label: "Shipped Orders",
            value: statusCounts.shipped,
            bg: "bg-blue-50",
            textColor: "text-blue-600",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
                />
              </svg>
            ),
          },
          {
            label: "Delivered Orders",
            value: statusCounts.delivered,
            bg: "bg-emerald-50",
            textColor: "text-emerald-600",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ),
          },
        ].map((card, idx) => (
          <div
            key={idx}
            className="border border-gray-200 rounded-2xl bg-white shadow-md px-4 sm:px-5 py-4 flex max-[420px]:flex-wrap items-center gap-2 sm:gap-4 shadow-sm"
          >
            <div
              className={`w-10 h-10 rounded-xl ${card.bg} ${card.textColor} flex items-center justify-center shrink-0`}
            >
              {card.icon}
            </div>
            <div className="max-[420px]:!w-full">
              <p className="text-xs sm:text-[16px] text-gray-600 font-medium">
                {card.label}
              </p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="">
        <div className="flex flex-col md:flex-row items-start sm:items-center justify-between gap-2 mb-4">
          {/* Status Tabs */}
          <div className="w-full lg:w-fit max-lg:overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden p-1.5">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  setCurrentPage(1);
                  setStatusFilter(tab.value);
                }}
                className={`whitespace-nowrap px-3 sm:px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                  statusFilter === tab.value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                <span
                  className={`text-sm xl:text-md font-semibold px-1.5 py-0 rounded-md ${
                    statusFilter === tab.value
                      ? "bg-gray-100 text-gray-600"
                      : "bg-gray-200/70 text-gray-400"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search by ID, name, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
            />
          </div>
        </div>

        <div className="sm:hidden space-y-4">
          {orders.length === 0 ? (
            <div className="max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4 text-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                  <MdPendingActions size={22} />
                </div>
                <p className="text-sm text-gray-500 font-medium">
                  No tracking orders found
                </p>
              </div>
            </div>
          ) : (
            orders.map((order) => {
              const status = order.status || "pending";

              return (
                <div
                  key={order._id || order.id}
                  className="max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4"
                >
                  {/* TOP */}
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                        Invoice
                      </p>
                      <p className="text-lg font-semibold text-blue-600">
                        {order.invoiceNumber || "--"}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">
                        ${order.invoiceDetails?.totalAmount || 0}
                      </p>

                      <p className="text-xs text-gray-500">
                        Tax: ${order.invoiceDetails?.totalTax || 0}
                      </p>
                    </div>
                  </div>

                  {/* DATES */}
                  <div className="grid grid-cols-2 gap-4 py-3 border-t border-b border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        Created
                      </p>
                      <p className="text-sm text-gray-700">
                        {formatDate(order.createdAt || order.createdDate)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        Shipping
                      </p>
                      <p className="text-sm text-gray-700">
                        {formatDate(order.shipDate || order.shippingDate)}
                      </p>
                    </div>
                  </div>

                  {/* STATUS */}
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2 items-center flex-wrap">
                      <p className="text-xs text-gray-500 capitalize">
                        {order.invoiceStatus === "partial_paid"
                          ? "Partial Paid"
                          : order.invoiceStatus || "—"}
                      </p>

                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${getStatusBadgeClass(
                          status,
                        )}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${getStatusBadgeDot(status)}`}
                        />
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>

                    {/* ACTION */}
                    <div className="gap-2 flex">
                      {order?.status == "shipped" && (
                        <button
                          title="Mark as Delivered"
                          onClick={() =>
                            handleStatusChange(
                              order._id || order.id,
                              "delivered",
                            )
                          }
                          className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                        >
                          <AiOutlineDeliveredProcedure />
                        </button>
                      )}
                      <button
                        title="View"
                        onClick={() =>
                          navigate(
                            `/order/tracking/detail/${order._id || order.id}`,
                          )
                        }
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Table Container */}
        <div className="max-sm:!hidden">
          <div className="overflow-auto border border-gray-200 rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Invoice",
                    "Invoice Status",
                    "Status",
                    "Shipping Date",
                    "Total Tax",
                    "Total Amount",
                    "Created Date",
                  ].map((header) => (
                    <th
                      key={header}
                      className="text-left py-3.5 px-4 font-semibold text-sm text-gray-800"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">
                          No tracking orders found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => {
                    const status = order.status || "pending";
                    const isDelivered = status.toLowerCase() === "delivered";
                    const isShippedOrDelivered =
                      status.toLowerCase() === "shipped" || isDelivered;
                    return (
                      <tr
                        key={order._id || order.id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        {/* Actions */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1">
                            {order?.status == "shipped" && (
                              <button
                                title="Mark as Delivered"
                                onClick={() =>
                                  handleStatusChange(
                                    order._id || order.id,
                                    "delivered",
                                  )
                                }
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                              >
                                <AiOutlineDeliveredProcedure />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/order/tracking/detail/${order._id || order.id}`,
                                )
                              }
                              title="View Details"
                              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#115B9D] transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                        {/* Order ID */}
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-xs font-semibold text-[#115B9D]">
                            {order.invoiceNumber || "--"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 max-w-[150px]">
                          <span className="text-sm capitalize text-gray-600">
                            {order.invoiceStatus == "partial_paid"
                              ? "Partial Paid"
                              : order.invoiceStatus || "—"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${getStatusBadgeClass(status)}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${getStatusBadgeDot(status)}`}
                              />
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                          </div>
                        </td>

                        {/* Shipping Date */}
                        <td className="py-3.5 px-4 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(order.shipDate || order.shippingDate)}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="text-sm capitalize text-gray-600">
                            ${order.invoiceDetails?.totalTax || "0"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="text-sm capitalize text-gray-600">
                            ${order.invoiceDetails?.totalAmount || "0"}
                          </span>
                        </td>
                        {/* Created Date */}
                        <td className="py-3.5 px-4 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(order.createdAt || order.createdDate)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalCount > limit && (
          <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-100 flex-wrap gap-3">
            <p className="text-gray-600 text-sm">
              {PaginationTextView(currentPage, limit, totalCount)}
            </p>
            <Pagination
              currentPage={currentPage}
              totalSize={totalCount}
              sizePerPage={limit}
              changeCurrentPage={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>

      {/* Edit Shipping Date Modal */}
      <Transition appear show={isDateModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setIsDateModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4"
                  >
                    Edit Shipped Date
                  </Dialog.Title>

                  <form onSubmit={handleDateSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1.5">
                        Shipping Date
                      </label>
                      <input
                        type="date"
                        value={shipDateVal}
                        onChange={(e) => setShipDateVal(e.target.value)}
                        className="h-10 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                        required
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-6">
                      <button
                        type="button"
                        onClick={() => setIsDateModalOpen(false)}
                        className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={submittingModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-semibold bg-[#115B9D] hover:opacity-90 rounded-xl text-white transition-colors"
                        disabled={submittingModal}
                      >
                        {submittingModal ? "Saving..." : "Save Date"}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Tracking URL Modal */}
      <Transition appear show={isTrackingModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setIsTrackingModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4"
                  >
                    Edit Tracking URL
                  </Dialog.Title>

                  <form onSubmit={handleTrackingSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1.5">
                        Tracking URL
                      </label>
                      <input
                        type="url"
                        placeholder="https://example.com/tracking"
                        value={trackingUrlVal}
                        onChange={(e) => setTrackingUrlVal(e.target.value)}
                        className="h-10 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                        required
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-6">
                      <button
                        type="button"
                        onClick={() => setIsTrackingModalOpen(false)}
                        className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={submittingModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-semibold bg-[#115B9D] hover:opacity-90 rounded-xl text-white transition-colors"
                        disabled={submittingModal}
                      >
                        {submittingModal ? "Saving..." : "Save Tracking URL"}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
